import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { Button, Modal } from "antd";
import Skeleton from "../skeleton";
import Env from "../../constant/env.json";
import "./terms_acceptance.css";

const TermsAcceptance = ({ checked, onChange, onBlur, disabled, error }) => {
  const [open, setOpen] = useState(false);
  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!open) return;

    const controller = new AbortController();
    setLoading(true);
    setLoadError(false);
    setTerms(null);

    // This public endpoint must also work before the user has a session.
    axios.get("terms-conditions/last", {
      baseURL: Env.base_url,
      signal: controller.signal,
      timeout: 15000,
    }).then(({ data }) => {
      if (controller.signal.aborted) return;
      if (typeof data?.content !== "string" || !data.content.trim()) {
        throw new Error("Terms content is unavailable");
      }
      setTerms(data);
    }).catch(() => {
      if (!controller.signal.aborted) setLoadError(true);
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });

    return () => controller.abort();
  }, [open, attempt]);

  const content = useMemo(() => DOMPurify.sanitize(terms?.content || "", {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ["style", "form", "input", "button", "textarea", "select"],
    FORBID_ATTR: ["style"],
  }), [terms]);
  const updatedAt = terms?.updateTime || terms?.createTime;
  const updatedDate = updatedAt ? new Date(updatedAt) : null;
  const formattedDate = updatedDate && !Number.isNaN(updatedDate.getTime())
    ? updatedDate.toLocaleDateString("en-US", {
      year: "numeric", month: "long", day: "numeric", timeZone: "UTC",
    })
    : null;

  const decide = (accepted) => {
    onChange(accepted);
    setOpen(false);
  };

  return (
    <div className="terms_acceptance">
      <div className={`terms_acceptance_row${error ? " terms_acceptance_invalid" : ""}`}>
        <input
          id="acceptedTerms"
          name="acceptedTerms"
          type="checkbox"
          required
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          onBlur={onBlur}
          disabled={disabled}
          aria-labelledby="terms_acceptance_label terms_acceptance_link"
          aria-invalid={Boolean(error)}
          aria-describedby={error ? "terms_acceptance_error" : undefined}
        />
        <div>
          <label id="terms_acceptance_label" htmlFor="acceptedTerms">I agree</label>{" "}
          <button
            id="terms_acceptance_link"
            className="terms_acceptance_link"
            type="button"
            disabled={disabled}
            onClick={() => setOpen(true)}
          >
            Naranel Terms &amp; Conditions
          </button>
        </div>
      </div>
      {error && <p id="terms_acceptance_error" className="terms_acceptance_error" role="alert">{error}</p>}
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        title="Usage & Acceptable Use Policy"
        footer={null}
        className="terms_modal"
        destroyOnHidden
      >
        {loading ? (
          <div
            className="terms_modal_loading"
            role="status"
            aria-label="Loading terms and conditions"
          >
            <div className="terms_modal_skeleton" aria-hidden="true">
              {[35, 45, 75, 100, 100, 95, 100, 65].map((width, index) => (
                <Skeleton
                  key={index}
                  className="terms_modal_skeleton_line"
                  style={{ width: `${width}%` }}
                />
              ))}
            </div>
          </div>
        ) : loadError ? (
          <div className="terms_modal_status" role="alert">
            <p>Unable to load terms and conditions. Please try again.</p>
            <Button onClick={() => setAttempt((value) => value + 1)}>Try again</Button>
          </div>
        ) : terms ? (
          <>
            {formattedDate && (
              <div className="terms_modal_updated">
                <span>Last Updated</span>
                <strong>{formattedDate}</strong>
              </div>
            )}
            <article className="terms_modal_html" dangerouslySetInnerHTML={{ __html: content }} />
            <div className="terms_modal_actions">
              <Button onClick={() => decide(false)}>I disagree</Button>
              <Button className="terms_modal_agree" onClick={() => decide(true)}>I agree</Button>
            </div>
          </>
        ) : null}
      </Modal>
    </div>
  );
};

export default TermsAcceptance;
